/**
 * 新守経済 (2026-09-06) — 閲覧課金のクライアント (SPEC §4.2)。
 *
 * サーバは「読むと払う」の殻 (post_locked.html) を
 * `<article class="post" data-locked="1" data-price="N">` で配る。本モジュールは
 *
 *   1. 殻が **画面に入った瞬間** (IntersectionObserver, threshold 0.25) にまとめて
 *      `POST /api/v1/view/unlock` を叩き、返ってきた本文つき HTML で article ごと
 *      差し替える (1 人 1 投稿 1 回。既払いなら無課金で本文が返る)。
 *   2. 残高不足は `data-locked="2"` にして「あと N 守で読めます」(N = 訊価 − 残高。
 *      残高が読めなければ「残高が足りません」) を出し、守残高が動いたときだけ
 *      (30 秒に 1 回まで) 再挑戦する。
 *   3. 払った瞬間の領収 (2026-09-16 監査 P-013 / P-026): 解錠中は殻の文言を
 *      「N 守 払っています…」に替え、差し替えたカードの時刻の横に「−N 守」を 3 秒
 *      だけ出す (町が払ったときは「町が払いました」)。同じ内容を aria-live の
 *      `#pv-live` にも流す。**出すのは守が動いたときだけ** — 読み返し (price 0)・
 *      何も動いていない (paid_by 2) には出さない。
 *   4. コンポーザの訊価メーター (`[data-price-meter]`) を 500ms デバウンスで
 *      `POST /api/v1/view/price` から更新する (読まれるたびに N 守 · いいねで M 守 ·
 *      写真を足すと K 守、0 守なら理由を短く)。
 *
 * ⚠ 気をつけていること
 *   - **同じ id を二重に送らない**: 送信中 (inFlight) と決着済み (settled) を持つ。
 *     二重送信そのものはサーバの UNIQUE(post_id, viewer_id) で二重課金にならないが、
 *     無駄な TX とレンダリングが増える。
 *   - **画面上端より上の要素は差し替えない**: 差し替えで高さが変わるとスクロールが
 *     飛ぶ。応答が返るまでに画面上へ流れていったものは HTML を持ち越し、次に
 *     見えたときに差し替える (課金は済んでいるので取りこぼしはない)。
 *   - **rAF に依存しない**: ヘッドレスの仮想時間では rAF が回らない
 *     (memory: headless-virtual-time-raf-trap)。タイマーとイベントだけで完結させる。
 *   - **再 bind の作法は timeline-media-unload.js / video-autoplay.js に合わせる**:
 *     `mp:timeline-appended` (document) / `timeline-prepended` (window) /
 *     `mp:navigated` (document) + `.col-main` の MutationObserver。
 */

import { ApiError, apiFetch } from "./api";
import { exposePaidFx, play as playFx, playBatch, shellRectOf, showPaying, type ShellRect } from "./paidfx";
import { instant } from "./new-posts-motion";

/** 1 リクエストの上限。internal/paidview.MaxUnlockBatch と同じ値 (超えると 400)。 */
const MAX_BATCH = 50;
/** IntersectionObserver のしきい値 (カードの 1/4 が見えたら「読んだ」)。 */
const THRESHOLD = 0.25;
/** 画面に入ってから送るまでの溜め (スクロールで一気に通り過ぎた分をまとめる)。 */
const DEBOUNCE_MS = 150;
/** 同じ投稿への再挑戦の間隔 (残高不足の無限ループ防止)。 */
const RETRY_MS = 30_000;
/** 訊価メーターのデバウンス。 */
const PRICE_DEBOUNCE_MS = 500;
/** MutationObserver からの再 bind の溜め。 */
const REBIND_MS = 100;

/** paidview.UnlockResult と 1:1 (v2/tmp/handoff/B3.md の表)。 */
interface UnlockResult {
  status: string; // "ok" | "insufficient" | "gone"
  html?: string;
  /**
   * ok: **この解錠で動いた守** (読者負担 = 読者が払った額 / 国庫負担 = 国庫が作者へ払った額 /
   *     既払い・無料 = 0)。insufficient: 要る訊価。gone: 常に 0。
   */
  price: number;
  /** 0 = 読者が払った / 1 = 町 (国庫) が払った / 2 = 何も動いていない。 */
  paid_by: number;
}

/** handler.unlockResp。Go の map[int64] は JSON で文字列キーになる。 */
interface UnlockResp {
  results?: Record<string, UnlockResult>;
}

/** handler.priceResp。 */
interface PriceResp {
  q: number;
  price: number;
  /** 読んでいない人が いいね すると払う合計 (= price × econ_like_mult)。 */
  like_price: number;
  /** 写真をもう 1 枚添えたときの訊価 (頭打ちなら price と同じ)。 */
  with_photo: number;
  /** price が 0 のときだけ "unreadable" / "duplicate" / "empty"。 */
  tier0_code: string;
  /** 同じ理由の一文 (訊価ページと同じ文言)。 */
  tier0_reason: string;
}

/**
 * 払った瞬間の領収 (カードに出す短い印と、読み上げ用の文)。
 *
 * kind は **守の出どころ**:
 *   self   読者が自分で払った (「−N 守」)
 *   town   町が払った (新人の国庫負担 / 町のおごり)
 *   author 書いた人が払った (試し読みの枠)
 *
 * ⚠ 見た目は self とそれ以外の 2 通りしか無い。**出どころで投稿カードを分けない**
 *   (2026-09-18 オーナー訂正) — 違いを伝えるのはこの 3 秒の印だけ。
 */
interface Receipt {
  stamp: string;
  live: string;
  kind: "self" | "town" | "author";
}

/** 領収の印を出しておく時間。 */
const STAMP_MS = 3000;
/** 印を引っ込めるアニメの長さ (CSS §231 の transition と揃える)。 */
const STAMP_LEAVE_MS = 320;

const LOCKED_SEL = 'article.post[data-locked="1"]';

let io: IntersectionObserver | null = null;
let mainObserver: MutationObserver | null = null;
let observedMain: Element | null = null;
let flushTimer: number | null = null;
let rebindTimer: number | null = null;
/** 自分が投げた mori:refresh を自分で拾って再挑戦しないための旗 (dispatch は同期)。 */
let selfRefresh = false;
let stopped = false;

/** 送信待ち (id → 殻の article)。 */
const pending = new Map<number, HTMLElement>();
/** 送信中。 */
const inFlight = new Set<number>();
/** 決着済み (ok)。二度と送らない。 */
const settled = new Set<number>();
/** 最後に送った時刻 (残高不足の再挑戦のクールダウン)。 */
const lastTry = new Map<number, number>();
/** 画面上へ流れていったため差し替えを持ち越した HTML (と、そのとき出す領収)。 */
const carried = new Map<number, { html: string; receipt: Receipt | null }>();
/**
 * 1 回の解錠応答で差し替えたカードの演出を溜める (send() の間だけ配列)。
 * 溜めてから画面の上から順に少しずつずらして再生する (paidfx.playBatch)。
 */
let fxBatch: Array<{ card: HTMLElement; rc: Receipt; shell: ShellRect | null }> | null = null;
/**
 * 差し替える前に測った殻の位置 (id → 位置)。1 回の応答ぶんを先にまとめて測る —
 * 差し替え (書く) と測る を交互にするとカードの数だけレイアウトが走る。
 */
const preShell = new Map<number, ShellRect | null>();

function postIDOf(el: Element): number {
  const n = Number(el.getAttribute("data-post-id") || "0");
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** 画面に少しでも掛かっているか (IO が再発火しない場面の手動判定)。 */
function onScreen(el: Element): boolean {
  const r = el.getBoundingClientRect();
  if (r.height === 0 && r.width === 0) return false;
  return r.bottom > 0 && r.top < (window.innerHeight || document.documentElement.clientHeight);
}

// ---------------------------------------------------------------------------
// 送信キュー
// ---------------------------------------------------------------------------

function schedule(): void {
  if (flushTimer !== null) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flush();
  }, DEBOUNCE_MS);
}

function queue(el: HTMLElement, force = false): void {
  if (stopped) return;
  if (!force && document.visibilityState !== "visible") return;
  const id = postIDOf(el);
  if (!id || settled.has(id) || inFlight.has(id) || pending.has(id)) return;
  if (!force) {
    const prev = lastTry.get(id);
    if (prev !== undefined && Date.now() - prev < RETRY_MS) return;
  }
  pending.set(id, el);
  schedule();
}

async function flush(): Promise<void> {
  if (stopped || pending.size === 0) return;
  // mode は要素ごとに決める (詳細ページは本投稿だけが post_detail で、祖先と返信は
  // post_card。ページ単位で決めると返信が詳細の体裁で返ってくる)。
  const card: number[] = [];
  const detail: number[] = [];
  const els = new Map<number, HTMLElement>();
  const rest = new Map<number, HTMLElement>();
  for (const [id, el] of pending) {
    if (!el.isConnected) continue;
    const bucket = el.classList.contains("post-detail") ? detail : card;
    if (bucket.length >= MAX_BATCH) {
      rest.set(id, el); // 次のフラッシュへ持ち越す
      continue;
    }
    bucket.push(id);
    els.set(id, el);
  }
  pending.clear();
  for (const [id, el] of rest) pending.set(id, el);
  if (rest.size > 0) schedule();
  await Promise.all([send(card, "card", els), send(detail, "detail", els)]);
}

async function send(ids: number[], mode: string, els: Map<number, HTMLElement>): Promise<void> {
  if (ids.length === 0) return;
  const now = Date.now();
  for (const id of ids) {
    inFlight.add(id);
    lastTry.set(id, now);
    // 解錠中: 鍵アイコンの代わりにネオンの輪 (v2.css §217 .mp-spin)。
    const el = els.get(id);
    if (el && el.isConnected) setUnlocking(el, true);
  }
  try {
    const res = await apiFetch<UnlockResp>("/api/v1/view/unlock", { body: { ids, mode } });
    const results = res.results || {};
    const receipts: Receipt[] = [];
    fxBatch = [];
    preShell.clear();
    for (const id of ids) {
      const el = els.get(id);
      if (el && el.isConnected && results[String(id)]?.status === "ok") preShell.set(id, shellRectOf(el));
    }
    for (const id of ids) {
      const r = results[String(id)];
      const el = els.get(id);
      if (!r || !el || !el.isConnected) continue;
      if (r.status === "ok") {
        settled.add(id);
        const rc = receiptFor(r);
        if (rc) receipts.push(rc);
        apply(id, el, r.html || "", rc);
      } else if (r.status === "insufficient") {
        markInsufficient(el, r.price);
      }
      // "gone" / 未知の status: 殻のまま残す。settled にはしない — 消えた・見えなく
      // なった投稿でも、見え方が戻れば (ブロック解除・談話室の再加入) クールダウン
      // (RETRY_MS) のあとにもう一度だけ試せるように。lastTry は上で刻んである。
    }
    flushFx();
    announce(receipts);
  } catch (e) {
    // 401 = ログアウト / セッション切れ。叩き続けても無駄なので止める。
    // それ以外 (5xx・ネットワーク断) はクールダウン後にまた試す。
    if (e instanceof ApiError && e.status === 401) stop();
  } finally {
    fxBatch = null;
    preShell.clear();
    for (const id of ids) {
      inFlight.delete(id);
      const el = els.get(id);
      if (el && el.isConnected) setUnlocking(el, false);
    }
    updateUnreadable();
  }
}

/**
 * 解錠中の見た目 (差し替え済みの article には何もしない)。
 * 「表示されると N 守」を「N 守 払っています…」(.post-locked-paying) に入れ替える。
 * 価格不明の殻 (Price 0) には paying が無いので、文言はそのまま輪だけ回す。
 */
function setUnlocking(el: HTMLElement, on: boolean): void {
  const box = el.querySelector<HTMLElement>(".post-locked");
  if (!box) return;
  el.classList.toggle("is-unlocking", on);
  // 払っている間の飾りと文言 (2026-09-19 デモ、web/ts/paidfx.ts)。いまの表示 (legacy) を
  // 選んでいるときだけ、いままでの輪 (.mp-spin) を出す。
  const decorated = showPaying(el, on);
  const line = box.querySelector<HTMLElement>(":scope > .post-locked-line") || box;
  const cur = line.querySelector<HTMLElement>(":scope > .mp-spin");
  if (on && !cur && !decorated) {
    const sp = document.createElement("span");
    sp.className = "mp-spin";
    sp.setAttribute("aria-hidden", "true");
    line.insertBefore(sp, line.firstChild);
  } else if (!on && cur) {
    cur.remove();
  }
  const msg = box.querySelector<HTMLElement>(".post-locked-msg");
  const paying = box.querySelector<HTMLElement>(".post-locked-paying");
  const short = el.getAttribute("data-locked") === "2";
  if (paying) paying.hidden = !on || short;
  if (msg) msg.hidden = short || (on && !!paying);
}

// ---------------------------------------------------------------------------
// 差し替え
// ---------------------------------------------------------------------------

function apply(id: number, el: HTMLElement, html: string, receipt: Receipt | null): void {
  if (!html) return;
  const r = el.getBoundingClientRect();
  if (r.bottom <= 0) {
    // 画面上端より上。ここで差し替えると高さが変わってスクロールが飛ぶので、
    // HTML を持ち越して次に見えたときに入れ替える (課金は済んでいる)。
    // 領収の印も一緒に持ち越す (見えたときに出す。読み上げは払った時点で済んでいる)。
    carried.set(id, { html, receipt });
    return;
  }
  swap(id, el, html, receipt);
}

function swap(id: number, el: HTMLElement, html: string, receipt: Receipt | null): void {
  const tpl = document.createElement("template");
  tpl.innerHTML = html.trim();
  const fresh = tpl.content.firstElementChild as HTMLElement | null;
  if (!fresh) return;
  carried.delete(id);
  if (io) io.unobserve(el);
  // 殻の識別線の位置は差し替える前に測る (演出「回路」がそこから始まる)。
  const shell = receipt ? (preShell.has(id) ? preShell.get(id) ?? null : shellRectOf(el)) : null;
  // 新着の印 (new-posts-motion.ts) を引き継ぐ。差し替えた要素では幕の動きは済んだ扱いにして、
  // 「どこまでが新着か」の光の筋だけを残す (.np-carry、v2.css §255)。
  if (el.dataset.np) {
    fresh.dataset.np = el.dataset.np;
    fresh.classList.add("np-new", "np-in", "np-carry");
    const i = el.style.getPropertyValue("--np-i");
    if (i) fresh.style.setProperty("--np-i", i);
  }
  // 差し替えで高さが変わる。カードの上端が画面の上より上 (= 読んでいる場所はこの
  // カードより下) のときは、伸びたぶんスクロール位置を合わせて、下の内容を 1px も
  // 動かさない (2026-09-23)。Safari にはスクロールアンカーが無く、上へ戻りながら
  // 持ち越した本文が入るたびに読んでいる行が跳んでいた。Chrome のアンカーは
  // instant() が止めるので、両方とも同じ動きになる。
  instant(() => {
    const before = el.getBoundingClientRect();
    el.replaceWith(fresh);
    if (before.top < 0 && before.bottom > 0) {
      const delta = fresh.getBoundingClientRect().bottom - before.bottom;
      if (Math.abs(delta) > 0.5) window.scrollBy(0, delta);
    }
  });
  if (receipt) stampReceipt(fresh, receipt, shell);
  // 既存スクリプト (動画の bind / 画像のメモリ解放 / 相対時刻) を再 bind させる。
  document.dispatchEvent(new CustomEvent("mp:timeline-appended", { detail: { nodes: [fresh] } }));
  // 守残高チップを更新する。自分が投げた分で再挑戦を走らせないよう旗を立てる
  // (dispatchEvent は同期なので、この 2 行の間でリスナが動く)。
  selfRefresh = true;
  document.dispatchEvent(new Event("mori:refresh"));
  selfRefresh = false;
  updateUnreadable();
}

// ---------------------------------------------------------------------------
// 領収 (−N 守) と読み上げ
// ---------------------------------------------------------------------------

/**
 * 解錠の結果から領収を作る。
 *
 *   paid_by 0 → 「−N 守」        読者が払った
 *   paid_by 1 → 「町が払いました」 新人の国庫負担 / 町のおごり
 *   paid_by 3 → 「書いた人が払いました」 試し読みの枠 (2026-09-18)
 *
 * 読み返し (既払い)・無料・paid_by 2 (何も動いていない)・insufficient・gone は null。
 *
 * ⚠ **支払い方法が違うだけの投稿に、専用の見た目を与えない** (2026-09-18 オーナー訂正)。
 *   カードは他と完全に同じで、違いを伝えるのは**ここの 3 秒の印だけ**。消えたら ✓ に戻る。
 *   バッジや枠線で「ただで読める」を強調すると、守の出どころに自分で気づくという
 *   趣旨そのものが壊れる。
 * ⚠ 名前を繰り返さない。カードに作者名が出ているので「書いた人」で足りる。
 * ⚠ 枠のときは price が 0 なので、上の `price > 0` の門より前に見る。
 */
function receiptFor(r: UnlockResult): Receipt | null {
  if (r.status !== "ok") return null;
  if (r.paid_by === 3) {
    return { stamp: "書いた人が払いました", live: "書いた人が払って本文を表示しました", kind: "author" };
  }
  if (!(r.price > 0)) return null;
  if (r.paid_by === 0) {
    return { stamp: `−${r.price} 守`, live: `${r.price} 守を払って本文を表示しました`, kind: "self" };
  }
  if (r.paid_by === 1) {
    return { stamp: "町が払いました", live: "町が払って本文を表示しました", kind: "town" };
  }
  return null;
}

/**
 * 領収を出す。2026-09-19 から **払った瞬間の演出** (web/ts/paidfx.ts) に置き換え中。
 * 解錠の応答を処理している間 (fxBatch がある間) は溜めて、あとでまとめてずらして再生する。
 * デモで「いまの印 (legacy)」を選んでいるときだけ、旧来の「−N 守」を出す。
 */
function stampReceipt(fresh: HTMLElement, rc: Receipt, shell: ShellRect | null = null): void {
  if (fxBatch) {
    fxBatch.push({ card: fresh, rc, shell });
    return;
  }
  if (!playFx(fresh, rc, { shell })) legacyStamp(fresh, rc);
}

/** 溜めた演出を画面の上から順に再生する。 */
function flushFx(): void {
  const batch = fxBatch;
  fxBatch = null;
  if (!batch || batch.length === 0) return;
  for (const it of playBatch(batch)) legacyStamp(it.card, it.rc);
}

/** 旧来の領収: 時刻の横に「−N 守」を 3 秒だけ置く (rAF は使わない)。比較用に残す。 */
function legacyStamp(fresh: HTMLElement, rc: Receipt): void {
  const s = document.createElement("span");
  // 自分で払ったときだけ額を強く出す。町でも書いた人でも「誰かが払った」は同じ見た目
  // (出どころで投稿を区別しない)。
  s.className = rc.kind === "self" ? "post-paid-stamp" : "post-paid-stamp post-paid-stamp--town";
  // 読み上げは #pv-live が担う (印を読むと「マイナス」と二重に聞こえる)。
  s.setAttribute("aria-hidden", "true");
  s.textContent = rc.stamp;
  // カード: ヘッダーの時刻 (✓ があればその後ろ)。詳細: 本文下の時刻の行。
  const anchor =
    fresh.querySelector<HTMLElement>(".posthead .post-paid-mark") ||
    fresh.querySelector<HTMLElement>(".posthead .time-toggle");
  if (anchor && anchor.parentNode) {
    anchor.parentNode.insertBefore(s, anchor.nextSibling);
  } else {
    const dt = fresh.querySelector<HTMLElement>(".post-detail-time");
    if (!dt) return;
    dt.appendChild(s);
  }
  window.setTimeout(() => {
    s.classList.add("is-leaving");
    window.setTimeout(() => s.remove(), STAMP_LEAVE_MS);
  }, STAMP_MS);
}

/** aria-live="polite" の読み上げ先。base.html に無ければ body に作る。 */
function liveRegion(): HTMLElement | null {
  let el = document.getElementById("pv-live");
  if (el) return el;
  if (!document.body) return null;
  el = document.createElement("div");
  el.id = "pv-live";
  el.className = "sr-only";
  el.setAttribute("aria-live", "polite");
  el.setAttribute("aria-atomic", "true");
  document.body.appendChild(el);
  return el;
}

/** 1 回の解錠でまとめて払ったぶんを 1 文で読み上げる (続けて読むと前の文が消されるため)。 */
function announce(receipts: Receipt[]): void {
  if (receipts.length === 0) return;
  let text = "";
  if (receipts.length === 1) {
    text = receipts[0].live;
  } else {
    let paid = 0;
    let own = 0;
    let town = 0;
    let author = 0;
    for (const rc of receipts) {
      switch (rc.kind) {
        case "town":
          town++;
          break;
        case "author":
          author++;
          break;
        default:
          own++;
          paid += Number(rc.stamp.replace(/[^0-9]/g, "")) || 0;
      }
    }
    const parts: string[] = [];
    if (own > 0) parts.push(`${own} 件の投稿に ${paid} 守を払いました`);
    if (town > 0) parts.push(`${town} 件は町が払いました`);
    // ⚠ 書いた人が払ったぶんを「町が払いました」に混ぜない (出どころが違う)。
    if (author > 0) parts.push(`${author} 件は書いた人が払いました`);
    text = parts.join("。") + "。本文を表示しました";
  }
  const live = liveRegion();
  if (!live) return;
  // 同じ文が続いても読み上げられるよう、いったん空にしてから入れる。
  live.textContent = "";
  window.setTimeout(() => {
    live.textContent = text;
  }, 60);
}

// ---------------------------------------------------------------------------
// 残高不足 (あと N 守)
// ---------------------------------------------------------------------------

/** いまの守残高。ヘッダーのチップ (data-mori) → 右サイドバーの数字の順に読む。読めなければ null。 */
function readBalance(): number | null {
  const chip = document.querySelector<HTMLElement>(".mori-chip[data-mori]");
  if (chip) {
    const n = Number(chip.getAttribute("data-mori"));
    if (Number.isFinite(n)) return n;
  }
  const txt = document.querySelector<HTMLElement>("[data-mori-text]");
  if (txt) {
    const raw = (txt.textContent || "").replace(/[,\s]/g, "").replace(/[−–]/g, "-");
    if (/^-?\d+$/.test(raw)) return Number(raw);
  }
  return null;
}

/** 殻の「あと N 守で読めます」を埋める。N が出せなければ「残高が足りません」。 */
function fillShortfall(el: HTMLElement): void {
  const need = Number(el.getAttribute("data-pv-need") || el.getAttribute("data-price") || "0");
  const bal = readBalance();
  const gap = bal === null ? 0 : need - Math.max(0, bal);
  const short = el.querySelector<HTMLElement>(".post-locked-short");
  const broke = el.querySelector<HTMLElement>(".post-locked-broke");
  const num = el.querySelector<HTMLElement>("[data-pv-short]");
  const ok = need > 0 && gap > 0;
  if (num && ok) num.textContent = String(gap);
  if (short) short.hidden = !ok;
  if (broke) broke.hidden = ok;
}

function markInsufficient(el: HTMLElement, need: number): void {
  el.setAttribute("data-locked", "2");
  if (need > 0) el.setAttribute("data-pv-need", String(need));
  const msg = el.querySelector<HTMLElement>(".post-locked-msg");
  if (msg) msg.hidden = true;
  const paying = el.querySelector<HTMLElement>(".post-locked-paying");
  if (paying) paying.hidden = true;
  const ins = el.querySelector<HTMLElement>(".post-locked-insufficient");
  if (ins) ins.hidden = false;
  fillShortfall(el);
}

function restoreLocked(el: HTMLElement): void {
  el.setAttribute("data-locked", "1");
  const msg = el.querySelector<HTMLElement>(".post-locked-msg");
  if (msg) msg.hidden = false;
  const ins = el.querySelector<HTMLElement>(".post-locked-insufficient");
  if (ins) ins.hidden = true;
}

/**
 * 守残高の表示が書き換わったら「あと N 守」を数え直す。チップは swap の mori:refresh を
 * 受けて非同期に取りに行くので、同じ解錠で先に払ったぶんが反映されるのはこのあと。
 * (チップの桁ロールには触らない。data-mori 属性と [data-mori-text] の文字だけを見る)
 */
let balanceObserver: MutationObserver | null = null;
function watchBalance(): void {
  if (balanceObserver || typeof MutationObserver === "undefined") return;
  const targets: Element[] = [];
  const chip = document.querySelector(".mori-chip[data-mori]");
  if (chip) targets.push(chip);
  document.querySelectorAll("[data-mori-text]").forEach((t) => targets.push(t));
  if (targets.length === 0) return;
  balanceObserver = new MutationObserver(() => {
    document.querySelectorAll<HTMLElement>('article.post[data-locked="2"]').forEach(fillShortfall);
  });
  for (const t of targets) {
    balanceObserver.observe(t, { attributes: true, attributeFilter: ["data-mori"], childList: true, characterData: true, subtree: true });
  }
}

/** TL 上の「読めない投稿 N 件」。0 なら親ごと隠す。 */
function updateUnreadable(): void {
  const bar = document.getElementById("paidview-bar");
  if (!bar) return;
  const n = document.querySelectorAll('article.post[data-locked="2"]').length;
  const count = bar.querySelector<HTMLElement>("[data-pv-unreadable-count]");
  if (count) count.textContent = String(n);
  const wrap = bar.querySelector<HTMLElement>("[data-pv-unreadable]");
  if (wrap) wrap.hidden = n === 0;
  // スマホでは帯の「きょう …」をヘッダーのチップが担うので、この帯は
  // 読めない投稿があるときだけ出す (v2.css §221)。:has() に頼らず旗で持つ。
  bar.classList.toggle("pv-has-unreadable", n > 0);
}

// ---------------------------------------------------------------------------
// 監視と再 bind
// ---------------------------------------------------------------------------

function onIntersect(entries: IntersectionObserverEntry[]): void {
  // 持ち越した本文が同じ通知で何枚も戻ってきたら、演出は上から順にずらして再生する。
  const own = fxBatch === null;
  if (own) fxBatch = [];
  try {
    for (const en of entries) {
      if (!en.isIntersecting) continue;
      const el = en.target as HTMLElement;
      const id = postIDOf(el);
      const held = id ? carried.get(id) : undefined;
      if (id && held !== undefined) {
        swap(id, el, held.html, held.receipt); // 持ち越した本文を、見えたこの瞬間に入れる
        continue;
      }
      queue(el);
    }
  } finally {
    if (own) flushFx(); // 途中で投げても溜めたままにしない (flushFx が fxBatch を null に戻す)
  }
}

function bind(): void {
  if (stopped || !io) return;
  const obs = io;
  document.querySelectorAll<HTMLElement>(LOCKED_SEL).forEach((el) => {
    if (el.dataset.pvBound === "1") return;
    el.dataset.pvBound = "1";
    obs.observe(el);
  });
  updateUnreadable();
}

function scheduleRebind(): void {
  if (rebindTimer !== null) return;
  rebindTimer = window.setTimeout(() => {
    rebindTimer = null;
    bind();
  }, REBIND_MS);
}

/** .col-main は softnav の即時詳細で作り直されることがあるので張り直す。 */
function attachMainObserver(): void {
  const main = document.querySelector(".col-main");
  if (!main || main === observedMain) return;
  if (mainObserver) mainObserver.disconnect();
  mainObserver = new MutationObserver(scheduleRebind);
  mainObserver.observe(main, { childList: true, subtree: true });
  observedMain = main;
}

/** 守残高が動いたら、残高不足で読めなかった投稿をもう一度だけ試す。 */
function retryInsufficient(): void {
  if (stopped || selfRefresh) return;
  const now = Date.now();
  let changed = false;
  document.querySelectorAll<HTMLElement>('article.post[data-locked="2"]').forEach((el) => {
    const id = postIDOf(el);
    if (!id) return;
    const prev = lastTry.get(id);
    if (prev !== undefined && now - prev < RETRY_MS) return;
    restoreLocked(el);
    changed = true;
    if (onScreen(el)) queue(el);
  });
  if (changed) updateUnreadable();
}

function onVisible(): void {
  if (document.visibilityState !== "visible") return;
  // 隠れている間の IO 発火は捨てているので、見えているものを拾い直す。
  document.querySelectorAll<HTMLElement>(LOCKED_SEL).forEach((el) => {
    if (onScreen(el)) queue(el);
  });
}

function stop(): void {
  stopped = true;
  pending.clear();
  if (io) io.disconnect();
  if (mainObserver) mainObserver.disconnect();
  if (flushTimer !== null) window.clearTimeout(flushTimer);
  flushTimer = null;
}


// ---------------------------------------------------------------------------
// コンポーザの訊価メーター
// ---------------------------------------------------------------------------

function meterBody(meter: HTMLElement): HTMLTextAreaElement | null {
  const forID = meter.getAttribute("data-for");
  if (forID) {
    const el = document.getElementById(forID);
    if (el instanceof HTMLTextAreaElement) return el;
  }
  const scope = meter.closest("form") || meter.parentElement;
  if (!scope) return null;
  return (
    scope.querySelector<HTMLTextAreaElement>("textarea.cm-body") ||
    scope.querySelector<HTMLTextAreaElement>('textarea[name="body"]')
  );
}

/** 添付の枚数と動画の有無は hidden input から数える (値の代入はイベントを出さない)。 */
function meterAttachments(scope: ParentNode): { photos: number; video: boolean } {
  const first =
    scope.querySelector<HTMLInputElement>("#cmImageUrl") ||
    scope.querySelector<HTMLInputElement>("input.cm-image-url");
  const extra = scope.querySelectorAll('input[name="image_urls[]"]').length;
  const vid =
    scope.querySelector<HTMLInputElement>("#cmVideoUrl") ||
    scope.querySelector<HTMLInputElement>("input.cm-video-url");
  return {
    photos: (first && first.value ? 1 : 0) + extra,
    video: !!(vid && vid.value),
  };
}


/**
 * 文字が変わるときだけ書く。フォームの MutationObserver (添付の検知) はメーター自身の
 * 書き換えも拾うので、同じ値を書き直すと 500ms ごとに /api/v1/view/price を叩き続ける。
 */
function setText(el: HTMLElement | null, v: string): void {
  if (el && el.textContent !== v) el.textContent = v;
}

/** 0 守の理由を 1 行に収まる長さで (全文は title へ)。 */
const TIER0_SHORT: Record<string, string> = {
  unreadable: "読めない投稿として 0 守になります",
  duplicate: "貼り直しとして 0 守になります",
  empty: "情報が無いので 0 守になります",
};

/**
 * メーターの 1 行を組み立てる (2026-09-16 監査 P-018)。
 *   price > 0: 「読まれるたびに N 守 · いいねで M 守 · 写真を足すと K 守」
 *              (いいねは like_price > price のとき、写真は with_photo > price のときだけ)
 *   price = 0: tier0_code を短い一文で。
 * 「質量」は内部語なので title に退避する。
 */
function renderMeter(meter: HTMLElement, priceEl: HTMLElement | null, res: PriceResp): void {
  const main = meter.querySelector<HTMLElement>("[data-pm-main]");
  const zero = meter.querySelector<HTMLElement>("[data-pm-zero]");
  const likeWrap = meter.querySelector<HTMLElement>("[data-pm-like-wrap]");
  const likeEl = meter.querySelector<HTMLElement>("[data-pm-like]");
  const photoWrap = meter.querySelector<HTMLElement>("[data-pm-photo-wrap]");
  const photoEl = meter.querySelector<HTMLElement>("[data-pm-photo]");
  const title = [`質量 Q = ${res.q}`];
  if (res.price > 0) {
    setText(priceEl, String(res.price));
    const like = Number(res.like_price) || 0;
    setText(likeEl, String(like));
    if (likeWrap) likeWrap.hidden = !(like > res.price);
    const photo = Number(res.with_photo) || 0;
    setText(photoEl, String(photo));
    if (photoWrap) photoWrap.hidden = !(photo > res.price);
    if (main) main.hidden = false;
    if (zero) zero.hidden = true;
    meter.classList.remove("is-zero");
  } else {
    const code = res.tier0_code || "empty";
    setText(zero, TIER0_SHORT[code] || TIER0_SHORT.empty);
    if (zero) zero.hidden = false;
    if (main) main.hidden = true;
    meter.classList.add("is-zero");
    if (res.tier0_reason) title.push(res.tier0_reason);
  }
  meter.title = title.join("\n");
}

function bindPriceMeter(meter: HTMLElement): void {
  if (meter.dataset.pvMeter === "1") return;
  meter.dataset.pvMeter = "1";
  const body = meterBody(meter);
  if (!body) return;
  const form = meter.closest("form");
  const scope: ParentNode = form || document;
  const priceEl = meter.querySelector<HTMLElement>("[data-pm-price]");
  let timer: number | null = null;
  let busy = false;
  let again = false;

  const run = async (): Promise<void> => {
    if (busy) {
      again = true;
      return;
    }
    const text = body.value || "";
    const att = meterAttachments(scope);
    if (!text.trim() && att.photos === 0 && !att.video) {
      meter.hidden = true;
      return;
    }
    busy = true;
    try {
      const res = await apiFetch<PriceResp>("/api/v1/view/price", {
        body: { body: text, photos: att.photos, video: att.video },
      });
      // mass が未配線 (テスト・起動直後) だと全部 0 で返る → 何も出さない。
      // 0 守には理由 (tier0_code) が付くので、理由があれば 0 でも出す。
      if (!res || (res.q === 0 && res.price === 0 && !res.tier0_code)) {
        meter.hidden = true;
        return;
      }
      renderMeter(meter, priceEl, res);
      meter.hidden = false;
    } catch {
      // 訊価は目安なので、失敗したら黙って前の値のままにする。
    } finally {
      busy = false;
      if (again) {
        again = false;
        void run();
      }
    }
  };

  const kick = (): void => {
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      timer = null;
      void run();
    }, PRICE_DEBOUNCE_MS);
  };

  body.addEventListener("input", kick);
  // 添付 (hidden input への値の代入) は input を出さないので、フォームの変化
  // (プレビューのタイル追加 / image_urls[] の append) からも拾う。
  if (form) {
    form.addEventListener("change", kick);
    new MutationObserver(kick).observe(form, { childList: true, subtree: true });
  }
}

function bindPriceMeters(): void {
  document.querySelectorAll<HTMLElement>("[data-price-meter]").forEach(bindPriceMeter);
}

// ---------------------------------------------------------------------------
// 入口
// ---------------------------------------------------------------------------

/** 閲覧課金のクライアントを起動する。殻が 1 つも無いページでも安全 (何もしない)。 */
export function initPaidview(): void {
  exposePaidFx(legacyStamp, setUnlocking);
  bindPriceMeters();
  watchBalance();
  document.addEventListener("mp:navigated", bindPriceMeters);
  if (typeof IntersectionObserver === "undefined") return;

  io = new IntersectionObserver(onIntersect, { threshold: THRESHOLD, rootMargin: "0px" });
  bind();
  attachMainObserver();

  document.addEventListener("mp:timeline-appended", bind);
  window.addEventListener("timeline-prepended", bind);
  document.addEventListener("mp:navigated", () => {
    attachMainObserver();
    bind();
  });
  document.addEventListener("visibilitychange", onVisible);
  // 守残高が動いたときだけ、残高不足だった投稿を試し直す。
  document.addEventListener("mori:refresh", retryInsufficient);
  document.addEventListener("mp:mori-changed", retryInsufficient);
}
